/**
 * mobiusLatticePipeline — a square lattice projected onto a Möbius
 * strip.
 *
 * A Möbius strip is a non-orientable surface with only one side.
 * We project a square lattice of nodes onto it, so the lattice
 * appears to twist and fold back on itself. The result is a
 * continuously twisting band of nodes that connects to its own
 * reverse.
 *
 * Innovations:
 *   - The lattice is embedded in 3D (using a UV grid on the strip)
 *     and then projected to 2D orthographically. The twist of the
 *     strip is animated so the lattice appears to fold.
 *   - Nodes have a per-node brightness that depends on their
 *     position along the strip — they pulse as they pass through
 *     the "front" of the viewing angle.
 *   - Edges connect lattice nodes; when the strip twists, edges
 *     appear to cross over themselves, creating impossible
 *     connections.
 *   - The "non-orientable" aspect is shown by drawing a tiny
 *     arrow that travels along the strip and returns pointing
 *     the opposite direction.
 *
 * Single fullscreen quad fragment shader.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MobiusLatticePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

// Position on the Möbius strip parameterized by (u, v) in [0, 1] x [-1, 1].
// The strip is a flat ribbon of width 1, radius 0.5, twisted by π over its
// full length.
vec3 mobiusPos(float u, float v, float release) {
  float angle = u * 3.14159 * 2.0 + release * 0.3;
  float r = 0.55;
  float twist = u * 3.14159 + release * 0.4;
  // The ribbon's local "up" direction rotates with u.
  vec3 center = vec3(r * cos(angle), 0.0, r * sin(angle));
  vec3 up = vec3(sin(twist) * 0.5, cos(twist), 0.0);
  vec3 outward = vec3(cos(angle), 0.0, sin(angle));
  // The ribbon has a slight curve to show 3D depth.
  vec3 right = vec3(-sin(angle), 0.0, cos(angle));
  return center + up * v * 0.4 + outward * 0.05 * sin(u * 6.2831 * 2.0);
}

// Project 3D -> 2D orthographic.
vec2 project(vec3 p) {
  return vec2(p.x, p.y + p.z * 0.3);
}

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;

  // Background — dark cool.
  float r = length(p);
  vec3 bg = mix(
    vec3(0.04, 0.06, 0.12),
    vec3(0.01, 0.01, 0.04),
    smoothstep(0.0, 0.7, r)
  );

  vec3 col = bg;

  // Draw the lattice nodes on the Möbius strip.
  const int U_NODES = 24;
  const int V_NODES = 5;
  float nodeSize = 0.015 + 0.005 * mid;
  for (int i = 0; i < U_NODES; i++) {
    for (int j = 0; j < V_NODES; j++) {
      float u = float(i) / float(U_NODES - 1);
      float v = (float(j) / float(V_NODES - 1)) * 2.0 - 1.0;
      vec3 pos3 = mobiusPos(u, v, uTime);
      vec2 pos = project(pos3);
      float d = length(p - pos);
      // Brightness depends on u — pulse traveling along the strip.
      float pulse = 0.5 + 0.5 * sin(u * 12.566 - uTime * 2.0);
      float brightness = 0.4 + 0.6 * pulse;
      // Color: gradient along u.
      vec3 nodeColor = vec3(
        0.5 + 0.5 * sin(u * 6.2831),
        0.5 + 0.5 * sin(u * 6.2831 + 2.094),
        0.5 + 0.5 * sin(u * 6.2831 + 4.189)
      );
      // Glow.
      float glow = exp(-d * d / (nodeSize * nodeSize * 4.0));
      float core = exp(-d * d / (nodeSize * nodeSize));
      col += nodeColor * glow * brightness * (0.5 + 0.5 * high);
      col += vec3(1.0, 0.95, 0.7) * core * brightness * 0.8;
    }
  }

  // Draw edges connecting adjacent nodes.
  // Edges along u (the "long" direction).
  for (int i = 0; i < U_NODES - 1; i++) {
    for (int j = 0; j < V_NODES; j++) {
      float u1 = float(i) / float(U_NODES - 1);
      float u2 = float(i + 1) / float(U_NODES - 1);
      float v = (float(j) / float(V_NODES - 1)) * 2.0 - 1.0;
      vec3 a3 = mobiusPos(u1, v, uTime);
      vec3 b3 = mobiusPos(u2, v, uTime);
      vec2 a = project(a3);
      vec2 b = project(b3);
      vec2 ab = b - a;
      float t = clamp(dot(p - a, ab) / max(dot(ab, ab), 0.0001), 0.0, 1.0);
      vec2 closest = a + ab * t;
      float d = length(p - closest);
      float thickness = 0.003 + 0.002 * mid;
      float lineMask = 1.0 - smoothstep(0.0, thickness, d);
      vec3 edgeColor = vec3(0.55, 0.85, 0.95);
      col += edgeColor * lineMask * 0.5;
    }
  }
  // Edges along v (the "short" direction).
  for (int i = 0; i < U_NODES; i++) {
    for (int j = 0; j < V_NODES - 1; j++) {
      float u = float(i) / float(U_NODES - 1);
      float v1 = (float(j) / float(V_NODES - 1)) * 2.0 - 1.0;
      float v2 = (float(j + 1) / float(V_NODES - 1)) * 2.0 - 1.0;
      vec3 a3 = mobiusPos(u, v1, uTime);
      vec3 b3 = mobiusPos(u, v2, uTime);
      vec2 a = project(a3);
      vec2 b = project(b3);
      vec2 ab = b - a;
      float t = clamp(dot(p - a, ab) / max(dot(ab, ab), 0.0001), 0.0, 1.0);
      vec2 closest = a + ab * t;
      float d = length(p - closest);
      float thickness = 0.003 + 0.002 * mid;
      float lineMask = 1.0 - smoothstep(0.0, thickness, d);
      vec3 edgeColor = vec3(0.95, 0.55, 0.85);
      col += edgeColor * lineMask * 0.5;
    }
  }

  // Tiny "arrow" that travels along the strip — to demonstrate
  // non-orientability. The arrow points "up" at u=0 and "down" at u=1.
  float arrowU = fract(uTime * 0.15);
  vec3 arrowPos3 = mobiusPos(arrowU, 0.0, uTime);
  vec2 arrowPos = project(arrowPos3);
  // The arrow's direction is the local "up" of the strip.
  // For u=0, up = (sin(0), cos(0), 0) = (0, 1, 0). For u=1, up = (sin(π), cos(π), 0) = (0, -1, 0).
  float twist = arrowU * 3.14159 + uTime * 0.4;
  // Arrow direction in 2D.
  vec2 arrowDir = vec2(0.0, cos(twist));
  // Draw a small arrowhead.
  float arrowD = length(p - arrowPos);
  if (arrowD < 0.04) {
    // Arrow tip.
    vec2 tipPos = arrowPos + arrowDir * 0.03;
    float tipD = length(p - tipPos);
    if (tipD < 0.008) {
      col += vec3(1.0, 0.95, 0.7) * 1.5;
    }
    // Tail.
    float tailD = length(p - arrowPos);
    if (tailD < 0.008) {
      col += vec3(1.0, 0.95, 0.7) * 1.0;
    }
  }

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  // Vignette.
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;

  outColor = vec4(col, 1.0);
}
`;

export function computeMobiusLatticeBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeMobiusLatticeRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMobiusLatticePipeline(gl: WebGL2RenderingContext): MobiusLatticePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMobiusLattice(
  gl: WebGL2RenderingContext,
  pipe: MobiusLatticePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
